import type { ReactNode } from 'react';
import {
  Box,
  Button,
  Grid,
  HStack,
  Icon,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverHeader,
  PopoverTrigger,
  Text,
  Tooltip,
  VisuallyHidden,
  usePrefersReducedMotion,
  useDisclosure,
} from '@chakra-ui/react';
import { ChevronRight } from 'lucide-react';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { djs } from '@duke-hq/djs';
import { Link as RouterLink } from 'react-router-dom';
import type { ContentTranslationUiStatus } from '@duke-hq/shared-constants/contentTranslationStatus';
import { iconSize, pxToRem } from '@duke-hq/ui/constants/scalable';
import { CoverageBar, coverageBarSegments, type CoverageBarCounts } from './CoverageBar';
import { LanguageStatusRow, languageListTemplateColumns } from './LanguageStatusRow';
import { TranslationStatusChip } from './TranslationStatusChip';
import { TranslationsTrigger } from './TranslationsToolbarPill';
import { isArrived } from './useStatusTransition';
import { useTransitionCounter } from './useTransitionCounter';
import { translationsMotion } from './translationsMotion';
import { rollupBarCounts, rollupDotStatus, type LanguageRollup } from './languageRollups';
import { useCourseTranslations } from './useCourseTranslations';
import type { TranslationsToolbarSummary } from './translationsSummary';
import { useTranslationEta } from './useTranslationEta';
import { useCourseTranslationAnnouncement } from './useCourseTranslationAnnouncement';
import { testIds } from './testIds';

const { durations, easings } = translationsMotion;

// Matches the resource panel's footer format.
const DATE_FORMAT = 'D MMM YYYY, h:mma';

// The batch equivalent of the resource editors' globe pill. Renders nothing
// until the flag is on and the course has languages configured; retry, CSV and
// per-resource detail stay on the coverage page.
export function CourseTranslationsToolbarPill({ courseId }: { courseId: string }) {
  const summary = useCourseTranslations(courseId);

  if (!summary.isActive) {
    return null;
  }

  return (
    <TranslationsToolbarPillContent
      summary={summary}
      coveragePath={`/courses/${courseId}/translations`}
      pillTestId={testIds.coursePill}
      popoverTestId={testIds.coursePopover}
    />
  );
}

// The non-ready cells worth naming under the fraction, in the shared lexicon.
// "Partly in English" is deliberately absent: the language rows carry it via the
// half-ring dot and hatched bar segment.
function riderParts(counts: TranslationsToolbarSummary['counts']): string[] {
  const parts: string[] = [];
  if (counts.inProgress > 0) {
    parts.push(t`${counts.inProgress} translating`);
  }
  if (counts.failed > 0) {
    parts.push(t`${counts.failed} failed`);
  }
  if (counts.stale > 0) {
    parts.push(t`${counts.stale} need review`);
  }
  return parts;
}

// A language landing in full is an arrival worth acknowledging; a partial or
// failed settle is not (isArrived excludes available-partial by design).
function isLanguageArrival(
  previous: ContentTranslationUiStatus | null,
  next: ContentTranslationUiStatus,
): boolean {
  const wasInFlight = previous === 'queued' || previous === 'generating';
  return wasInFlight && isArrived(next);
}

// One ledger row on the shared LanguageStatusRow (its bdi isolation is what keeps
// RTL endonyms from flipping the row). Status slot reuses the resource popover's
// chip; the full breakdown rides the tooltip and accessible name.
function LanguageLedgerRow({
  rollup,
  breathIndex,
}: {
  rollup: LanguageRollup;
  breathIndex: number;
}) {
  const motionDisabled = usePrefersReducedMotion();
  const dotStatus = rollupDotStatus(rollup);
  const summary = coverageBarSegments(rollupBarCounts(rollup)).summary;
  const arrivalKey = useTransitionCounter(dotStatus, isLanguageArrival, !motionDisabled);

  return (
    <Tooltip label={`${rollup.englishName}: ${summary}`} openDelay={200}>
      <LanguageStatusRow
        code={rollup.code}
        endonym={rollup.endonym}
        englishName={rollup.englishName}
        rowProps={{
          position: 'relative',
          px: 3,
          py: 1.5,
          borderBottomWidth: '1px',
          borderColor: 'border.default',
          _last: { borderBottomWidth: 0 },
          'aria-label': `${rollup.englishName}: ${summary}`,
          'data-testid': testIds.coursePopoverLanguageRow,
        }}
        // One green wash per full landing, keyed by the arrival count so it
        // replays on each landing and never on mount of an already-done row.
        overlay={
          arrivalKey > 0 ? (
            <Box
              key={arrivalKey}
              position="absolute"
              inset={0}
              bg="green.50"
              pointerEvents="none"
              aria-hidden
              sx={{
                animation: `translationRowWash ${durations.wash}ms ${easings.standard} forwards`,
                '@keyframes translationRowWash': {
                  '0%': { opacity: 1 },
                  '100%': { opacity: 0 },
                },
              }}
            />
          ) : null
        }
        status={
          <HStack spacing={2}>
            <TranslationStatusChip status={dotStatus} breathIndex={breathIndex} />
            <Text
              fontSize="xs"
              color="fg.muted"
              w={pxToRem(40)}
              textAlign="right"
              sx={{ fontVariantNumeric: 'tabular-nums' }}
            >
              {rollup.available}/{rollup.total}
            </Text>
          </HStack>
        }
      />
    </Tooltip>
  );
}

// The single line under the fraction, whichever fact fits the phase: what's
// outstanding while a run is live, then the caller's empty hint before anything
// exists, then how fresh the coverage is. `emptyDetail` is the hint (course: how
// translations start; unit: none, since it's measured against every language and
// publishing only fills those a course offers).
function detailLine(
  riders: string[],
  etaLabel: string | null,
  ready: number,
  lastUpdatedAt: Date | null,
  emptyDetail: string | null,
): string | null {
  const activity = [...riders, ...(etaLabel === null ? [] : [etaLabel])];
  if (activity.length > 0) {
    return activity.join(' · ');
  }
  if (ready === 0) {
    return emptyDetail;
  }
  if (lastUpdatedAt !== null) {
    return t`Last updated ${djs(lastUpdatedAt).format(DATE_FORMAT)}`;
  }
  return null;
}

// Presentational, so Storybook renders every state without tRPC. The footer is
// caller-supplied: the course pill passes a "View full coverage" link via
// `coveragePath`; the unit pill passes its own `footer` (a Translate action and
// the published-only note). At most one is used, `footer` winning.
export function TranslationsToolbarSummaryView({
  ready,
  total,
  riders,
  barCounts,
  rollups,
  etaLabel,
  lastUpdatedAt,
  emptyDetail,
  coveragePath,
  footer,
}: {
  ready: number;
  total: number;
  riders: string[];
  barCounts: CoverageBarCounts;
  rollups: LanguageRollup[];
  etaLabel: string | null;
  lastUpdatedAt: Date | null;
  // The line shown before anything is translated. Undefined keeps the course
  // default ("Generated when you publish."); null shows no line.
  emptyDetail?: string | null;
  coveragePath?: string;
  footer?: ReactNode;
}) {
  const resolvedEmptyDetail =
    emptyDetail === undefined ? t`Generated when you publish.` : emptyDetail;
  const detail = detailLine(riders, etaLabel, ready, lastUpdatedAt, resolvedEmptyDetail);

  return (
    <Box>
      <Box px={3} py={2} borderBottomWidth="1px" borderColor="border.default">
        <Text
          fontSize="sm"
          fontWeight="medium"
          sx={{ fontVariantNumeric: 'tabular-nums' }}
          data-testid={testIds.coursePopoverFraction}
        >
          {ready === total ? (
            <Trans>All {total} translations ready</Trans>
          ) : (
            <Trans>
              {ready} of {total} translations ready
            </Trans>
          )}
        </Text>
        <Tooltip label={coverageBarSegments(barCounts).summary}>
          <Box mt={1.5} mb={detail !== null ? 1 : 0.5}>
            <CoverageBar counts={barCounts} />
          </Box>
        </Tooltip>
        {detail !== null ? (
          <Text fontSize="xs" color="fg.muted" sx={{ fontVariantNumeric: 'tabular-nums' }}>
            {detail}
          </Text>
        ) : null}
      </Box>
      {/* Sized so the full default language set (7 rows) shows without clipping;
          only longer sets scroll, keeping the footer pinned and reachable.
          Subgrid, so the name and status columns align down the list exactly as
          in the resource panel. */}
      <Grid
        templateColumns={languageListTemplateColumns()}
        columnGap={3}
        maxH={pxToRem(380)}
        overflowY="auto"
      >
        {rollups.map((rollup, index) => (
          <LanguageLedgerRow key={rollup.code} rollup={rollup} breathIndex={index} />
        ))}
      </Grid>
      {footer ??
        (coveragePath !== undefined ? (
          <Box px={2} py={2} borderTopWidth="1px" borderColor="border.default">
            <Button
              as={RouterLink}
              to={coveragePath}
              size="sm"
              variant="ghost"
              w="full"
              justifyContent="space-between"
              rightIcon={<Icon as={ChevronRight} boxSize={iconSize.sm} />}
              data-testid={testIds.coursePopoverViewCoverage}
            >
              <Trans>View course coverage</Trans>
            </Button>
          </Box>
        ) : null)}
    </Box>
  );
}

// The pill trigger + popover scaffold, shared by the course and unit pills. The
// caller supplies the coverage data (`summary`), the footer (a coverage link via
// `coveragePath`, or a bespoke `footer` node), and the test ids for its level.
export function TranslationsToolbarPillContent({
  summary,
  coveragePath,
  footer,
  headerAction,
  emptyDetail,
  pillTestId,
  popoverTestId,
}: {
  summary: TranslationsToolbarSummary;
  coveragePath?: string;
  footer?: ReactNode;
  // An action rendered in the popover header, beside the title (the unit pill's
  // Translate sits here rather than as a stray footer button).
  headerAction?: ReactNode;
  emptyDetail?: string | null;
  pillTestId: string;
  popoverTestId: string;
}) {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const motionDisabled = usePrefersReducedMotion();

  const { counts, barCounts, ready, total, rollups, lastUpdatedAt } = summary;
  const running = counts.inProgress > 0;
  const eta = useTranslationEta({ ready, total, running });
  const announcement = useCourseTranslationAnnouncement({
    ready,
    total,
    failed: counts.failed,
    running,
  });

  return (
    <>
      {/* Co-located with the pill so a screen-reader user gets batch progress
          without opening the popover. Kickoff is left to the publish toast. */}
      <VisuallyHidden aria-live="polite" aria-atomic="true">
        {announcement}
      </VisuallyHidden>
      <Popover placement="bottom-end" isOpen={isOpen} onOpen={onOpen} onClose={onClose}>
        <PopoverTrigger>
          <Box data-testid={pillTestId}>
            <TranslationsTrigger pill={summary.pill} />
          </Box>
        </PopoverTrigger>
        <PopoverContent
          width={pxToRem(360)}
          transformOrigin="top right"
          motionProps={motionDisabled ? { transition: { duration: 0 } } : undefined}
          data-testid={popoverTestId}
        >
          <PopoverArrow />
          <PopoverHeader>
            <HStack justify="space-between" spacing={2}>
              <Text fontSize="sm" fontWeight="semibold">
                <Trans>Translations</Trans>
              </Text>
              {headerAction}
            </HStack>
          </PopoverHeader>
          <PopoverBody p={0}>
            <TranslationsToolbarSummaryView
              ready={ready}
              total={total}
              riders={riderParts(counts)}
              barCounts={barCounts}
              rollups={rollups}
              etaLabel={running ? eta : null}
              lastUpdatedAt={lastUpdatedAt}
              emptyDetail={emptyDetail}
              coveragePath={coveragePath}
              footer={footer}
            />
          </PopoverBody>
        </PopoverContent>
      </Popover>
    </>
  );
}
